// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Plugins
import {
  plcBooleanToStatus,
  plcToOperationMode,
} from '../../../../../../plugins/crusher';
import { DASHBOARD_NULLISH_TEXT } from '../../../../../../plugins/general';

// Components
import { PLCCard, PLCCardType } from '../../../../../../components/PLCCard';

// Layout
import { Container, CardsContainer } from './PLC.style';

// View
import { PLCCabinetProps } from './PLC.types';
import { StatusIndicatorStatus } from '../../../../../../layout/elements/StatusIndicator';

function PLC({
  className,
  unitData,
  edgeStatus,
  testId,
}: PLCCabinetProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  const get24VDCInputItems = (): {
    number: string;
    text: string;
    showStatusIndicator: boolean;
    status: StatusIndicatorStatus;
  }[] => [
    {
      number: '00',
      text: t('view.crusher.maintenance.plcModule.hpuControlEnable'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.systemControlPower,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '01',
      text: t('view.crusher.maintenance.plcModule.hpuRemote'),
      showStatusIndicator: true,
      status: plcToOperationMode(
        unitData?.operationModeLocalRemote,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '02',
      text: t('view.crusher.maintenance.plcModule.hpuTrampReset'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputTrampReset,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '03',
      text: t('view.crusher.maintenance.plcModule.hpuTrampClear'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputTrampClear,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '04',
      text: t('view.crusher.maintenance.plcModule.hpuBowlOpen'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputCssOpen,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '05',
      text: t('view.crusher.maintenance.plcModule.hpuBowlClose'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputCssClose,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '06',
      text: '',
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        null,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '07',
      text: t('view.crusher.maintenance.plcModule.hpuBowlHighSpeed'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputHighSpeed,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
  ];

  const get24VDCOutputItems = (): {
    number: string;
    text: string;
    showStatusIndicator: boolean;
    status: StatusIndicatorStatus;
  }[] => [
    {
      number: '00',
      text: t('view.crusher.maintenance.plcModule.hpuRemoteBoxAlarm'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputRemoteBoxAlarm,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '01',
      text: t('view.crusher.maintenance.plcModule.hpuRemoteCrusherEnable'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputRemoteCrusherEnable,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '02',
      text: t('view.crusher.maintenance.plcModule.hpuRemoteBoxPower'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputRemoteBoxPower,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
  ];

  const getAnalogItems = (): { text: string; value?: string }[] => [
    { text: 'Vin0+' },
    { text: 'l in0+' },
    { text: 'V/lin0-' },
    { text: 'Vin1+' },
    { text: 'l in1+' },
    { text: 'V/lin1-' },
    { text: 'Vin2+' },
    {
      text: 'l in2+',
      value: `${
        unitData?.analogCrMotor1Amps || DASHBOARD_NULLISH_TEXT} 
        ${t('view.crusher.maintenance.plcModule.crushMtr1Ct')}`,
    },
    { text: 'V/lin2-' },
    { text: 'Vin3+' },
    {
      text: t('view.crusher.maintenance.plcModule.iIn3Plus'),
      value: `${unitData?.analogCrMotor2Amps || DASHBOARD_NULLISH_TEXT}
       ${t('view.crusher.maintenance.plcModule.crushMtr2Ct')}`,
    },
    { text: t('view.crusher.maintenance.plcModule.vin3Plus') },
    { text: 'l out0+' },
    { text: 'Com' },
    {
      text: t('view.crusher.maintenance.plcModule.lout1Plus'),
      value: `${unitData?.analogFeedSpeed || DASHBOARD_NULLISH_TEXT} 
      ${t('view.crusher.maintenance.plcModule.fdrSpdCtrl')}`,
    },
    { text: t('view.crusher.maintenance.plcModule.com') },
  ];

  const getDCInputItems = () => [
    {
      number: '00',
      text: t('view.crusher.maintenance.plcModule.eStop'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.eStop,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '01',
      text: t('view.crusher.maintenance.plcModule.autoControl'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputPlcControl,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '02',
      text: t('view.crusher.maintenance.plcModule.crusherMotor1Aux'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputCrMotor1Aux,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '03',
      text: t('view.crusher.maintenance.plcModule.crusherMotor2Aux'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputCrMotor2Aux,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '04',
      text: t('view.crusher.maintenance.plcModule.lubePump1Aux'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputLubePump1Aux,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '05',
      text: t('view.crusher.maintenance.plcModule.lubePump2Aux'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputLubePump2Aux,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '06',
      text: t('view.crusher.maintenance.plcModule.lubeAirCoolerAux'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputLubeCooler1Aux,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '07',
      text: t('view.crusher.maintenance.plcModule.lubeHeater1Aux'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputLubeHeater1Aux,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '08',
      text: t('view.crusher.maintenance.plcModule.lubeHeater2Aux'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputLubeHeager2Aux,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '09',
      text: t('view.crusher.maintenance.plcModule.hpuMotorAux'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputHpuMotoAux,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '10',
      text: t('view.crusher.maintenance.plcModule.dischargeConveyorMotorAux'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputDischargeConveyorAux,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '11',
      text: t('view.crusher.maintenance.plcModule.counterShaftBlowerMotorAux'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputCountershaftBlowerAux,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '12',
      text: t('view.crusher.maintenance.plcModule.feedConveyorMotorAux'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputFeedDriveAux,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '13',
      text: t('view.crusher.maintenance.plcModule.remoteAutoSequenceStart'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputRemoteAutoStart,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '14',
      text: t('view.crusher.maintenance.plcModule.remoteAutoSequenceStop'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dInputRemoteAutoStop,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
  ];

  const getDCOutputItems = () => [
    {
      number: '00',
      text: t('view.crusher.maintenance.plcModule.crusherMotor1Run'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputMotor1Run,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '01',
      text: t('view.crusher.maintenance.plcModule.crusherMotor2Run'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputMotor2Run,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '02',
      text: t('view.crusher.maintenance.plcModule.lubePumpMotor1Run'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputLubePump1Run,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '03',
      text: t('view.crusher.maintenance.plcModule.lubePumpMotor2Run'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputLubePump2Run,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '04',
      text: t('view.crusher.maintenance.plcModule.lubeAirCoolerRun'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputLubeCooler1Run,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '05',
      text: t('view.crusher.maintenance.plcModule.lubeHeater1Run'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputLubeHeaterRun,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '06',
      text: t('view.crusher.maintenance.plcModule.lubeHeater2Run'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputLubeHeaterRun2,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '07',
      text: t('view.crusher.maintenance.plcModule.hpuMotorRun'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputHpuMotorRun,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '08',
      text: t('view.crusher.maintenance.plcModule.dischargeConveyor'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputDischargeConveyor,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '09',
      text: t('view.crusher.maintenance.plcModule.countershaftBlowerRun'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputCounterShaftBlowerRun,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '10',
      text: t('view.crusher.maintenance.plcModule.feedConveyorRun'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputFeedConveyorRun,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '11',
      text: t('view.crusher.maintenance.plcModule.crusherPermissiveOk'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputCrusherPermissiveOk,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '12',
      text: t('view.crusher.maintenance.plcModule.lubePermissiveOk'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputLubePermissiveOk,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '13',
      text: t('view.crusher.maintenance.plcModule.feedPermissiveOk'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputFeedPermissiveOk,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '14',
      text: t('view.crusher.maintenance.plcModule.remoteSequenceStartAux'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputProgSequenceStart,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      number: '15',
      text: t('view.crusher.maintenance.plcModule.warningHorn'),
      showStatusIndicator: true,
      status: plcBooleanToStatus(
        unitData?.dOutputWarningHorn,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
  ];

  return (
    <Container
      className={className}
      data-testid={testId}
    >
      <CardsContainer>
        <PLCCard
          text={t('view.crusher.maintenance.plcModule.plc24VdcInputs')}
          items={get24VDCInputItems()}
          type={PLCCardType.input}
          testId="plc-24vdc-inputs"
        />
        <PLCCard
          text={t('view.crusher.maintenance.plcModule.plc24VdcOutputs')}
          items={get24VDCOutputItems()}
          type={PLCCardType.output}
          testId="plc-24vdc-outputs"
        />
        <PLCCard
          text={t('view.crusher.maintenance.plcModule.plcAnalog')}
          items={getAnalogItems()}
          type={PLCCardType.analog}
          testId="plc-analog"
        />
        <PLCCard
          text={t('view.crusher.maintenance.plcModule.dcInputCard')}
          items={getDCInputItems()}
          type={PLCCardType.input}
          testId="plc-dc-input"
        />
        <PLCCard
          text={t('view.crusher.maintenance.plcModule.dcOutputCard')}
          items={getDCOutputItems()}
          type={PLCCardType.output}
          testId="plc-dc-output"
        />
      </CardsContainer>
    </Container>
  );
}

export { PLC };
